import { alpha } from "@mui/material/styles";

declare module "@mui/material/styles" {
  interface PaletteColor {
    100: string;
    200: string;
    300: string;
    400: string;
    red?: string;
    purple?: string;
    yellow?: string;
  }
}

// Brand: deep emerald — calm, focused, education-friendly.
// Light theme uses emerald-700 for ink-on-paper contrast.
// Dark theme uses softer emerald-400 so it reads without glaring on dark surfaces.
const emeraldLight = "#047857"; // emerald-700
const emeraldLightDark = "#065F46"; // emerald-800
const emeraldDark = "#34D399"; // emerald-400 — softer on the eye than 500
const emeraldDarkDark = "#10B981"; // emerald-500 for hover

const buildPrimary = (main: string, dark: string, surfaceLight: string) => ({
  light: surfaceLight,
  main,
  dark,
  100: alpha(main, 0.06),
  200: alpha(main, 0.12),
  300: alpha(main, 0.2),
  400: alpha(main, 0.32),
  red: "#E11D48",
  purple: "#7C3AED",
  yellow: "#D97706",
});

export const primaryLight = buildPrimary(emeraldLight, emeraldLightDark, "#ECFDF5");
export const primaryDark = buildPrimary(emeraldDark, emeraldDarkDark, alpha(emeraldDark, 0.1));

// Backwards-compat default export: light brand.
export const primary = primaryLight;

const teal = "#0D9488"; // teal-600 — used for "info" alerts in light
export const secondary = {
  light: "#F0FDFA",
  main: teal,
  dark: "#0F766E",
  100: alpha(teal, 0.06),
  200: alpha(teal, 0.12),
  300: alpha(teal, 0.2),
  400: alpha(teal, 0.32),
};

export const info = {
  light: "#F0F9FF",
  main: "#0284C7",
  dark: "#0369A1",
};
export const success = {
  light: "#F0FDF4",
  main: "#16A34A",
  dark: "#15803D",
};
export const warning = {
  light: "#FFFBEB",
  main: "#D97706",
  dark: "#B45309",
};
export const error = {
  light: "#FEF2F2",
  main: "#DC2626",
  dark: "#B91C1C",
};

// Warm neutrals — Tailwind stone. Slightly warm, very legible. Replaces zinc.
export const greyLight = {
  50: "#FAFAF9",
  100: "#F5F5F4",
  200: "#E7E5E4",
  300: "#D6D3D1",
  400: "#A8A29E",
  500: "#78716C",
  600: "#57534E",
  700: "#44403C",
  800: "#292524",
  900: "#1C1917",
};

export const greyDark = {
  900: "#E7E5E4",
  800: "#D6D3D1",
  700: "#A8A29E",
  600: "#78716C",
  500: "#57534E",
  400: "#44403C",
  300: "#322E2A",
  200: "#26221F",
  100: "#1F1B17",
  50: "#171513",
};

export const textLight = {
  primary: "#0C0A09",
  secondary: "#57534E",
  disabled: "#A8A29E",
};

export const textDark = {
  // Slightly off-white instead of bright #FAFAF9 — easier on the eye.
  primary: "#E7E5E4",
  secondary: "#A8A29E",
  disabled: "#78716C",
};

export const actionLight = {
  activatedOpacity: 0.12,
  active: alpha("#0C0A09", 0.54),
  disabled: greyLight[300],
  disabledBackground: alpha("#0C0A09", 0.05),
  disabledOpacity: 0.38,
  focus: alpha("#0C0A09", 0.06),
  focusOpacity: 0.06,
  hover: alpha("#0C0A09", 0.04),
  hoverOpacity: 0.04,
  selected: greyLight[100],
  selectedOpacity: 0.08,
};

export const actionDark = {
  activatedOpacity: 0.14,
  active: alpha("#E7E5E4", 0.7),
  disabled: greyDark[300],
  disabledBackground: alpha("#E7E5E4", 0.05),
  disabledOpacity: 0.38,
  focus: alpha("#E7E5E4", 0.06),
  focusOpacity: 0.06,
  hover: alpha("#E7E5E4", 0.05),
  hoverOpacity: 0.05,
  selected: alpha("#E7E5E4", 0.07),
  selectedOpacity: 0.07,
};

const baseAlerts = { info, error, success, warning, secondary };

export const lightPalette = {
  ...baseAlerts,
  primary: primaryLight,
  mode: "light",
  grey: greyLight,
  text: textLight,
  action: actionLight,
  divider: greyLight[200],
  // Warm off-white default. Paper stays pure white for contrast.
  background: { default: "#FBFAF7", paper: "#FFFFFF" },
};

export const darkPalette = {
  ...baseAlerts,
  primary: primaryDark,
  mode: "dark",
  grey: greyDark,
  text: textDark,
  action: actionDark,
  // Slightly more visible divider so cards & rows have shape.
  divider: alpha("#E7E5E4", 0.09),
  // Warm dark base — never pitch black. Paper one notch lighter so cards "lift".
  background: { default: "#171513", paper: "#1F1B17" },
};
